import React, { useCallback, useEffect, useMemo, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { LANGUAGES } from "@/lib/i18n";
import {
  Users, UserPlus, Activity, ClipboardCheck, CheckCircle2, Download, BarChart3,
  TrendingUp, LineChart as LineChartIcon, Layers, GraduationCap, BookOpen,
  ChevronLeft, ChevronRight, RotateCcw, TrendingDown, Minus,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from "recharts";

/**
 * The period-scoped half of this page (everything driven by the selector)
 * hits GET /admin/analytics/summary. The lifetime half — total users, the
 * per-story pass-rate table, role/language demographics — comes from the
 * original GET /admin/analytics once on mount and never refetches, so
 * switching periods can never make it re-animate or flicker (Bug #2 in the
 * brief this page was built against).
 */

const TEAL = "#5E9A93";
const CORAL = "#D2806D";
const SLATE = "#5B6882";
const BORDER = "#E5DFC8";

const PERIODS = [
  { v: "daily", l: "Daily" },
  { v: "weekly", l: "Weekly" },
  { v: "monthly", l: "Monthly" },
  { v: "6m", l: "6M" },
  { v: "yearly", l: "Yearly" },
  { v: "custom", l: "Custom" },
];

const ROLE_LABEL = { student: "Students", parent: "Parents", teacher: "Teachers", admin: "Admins" };
const LANG_LABEL = Object.fromEntries(LANGUAGES.map((l) => [l.code, l.label]));

function StatTile({ icon: Icon, value, label, lifetime }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E5DFC8] p-5 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="rounded-xl bg-hv-tealTint p-2 inline-flex">
          <Icon className="w-5 h-5 text-hv-teal" strokeWidth={2.4} />
        </div>
        {lifetime && (
          <span className="text-[9px] uppercase tracking-wider font-bold text-hv-slate/70 mt-1">All-time</span>
        )}
      </div>
      <p className="hv-heading text-3xl mt-3">{value}</p>
      <p className="text-sm text-hv-slate mt-1">{label}</p>
    </div>
  );
}

function DeltaBadge({ delta }) {
  if (!delta) return <span className="h-4 block" />;
  const { direction, pct_change } = delta;
  const Icon = direction === "up" ? TrendingUp : direction === "down" ? TrendingDown : Minus;
  const color = direction === "up" ? "text-hv-teal" : direction === "down" ? "text-hv-coral" : "text-hv-slate";
  const sign = direction === "up" ? "+" : direction === "down" ? "" : "";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold ${color}`}>
      <Icon className="w-3.5 h-3.5" strokeWidth={2.8} />
      {sign}{pct_change}% <span className="font-normal text-hv-slate">vs previous</span>
    </span>
  );
}

function MetricTile({ icon: Icon, value, label, delta }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E5DFC8] p-5 shadow-sm" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="rounded-xl bg-hv-tealTint p-2 inline-flex">
        <Icon className="w-5 h-5 text-hv-teal" strokeWidth={2.4} />
      </div>
      <p className="hv-heading text-3xl mt-3">{value}</p>
      <p className="text-sm text-hv-slate mt-1">{label}</p>
      <div className="mt-2"><DeltaBadge delta={delta} /></div>
    </div>
  );
}

function ChartCard({ icon: Icon, title, sub, children, dim, testid, className = "" }) {
  return (
    <div
      className={`rounded-2xl bg-white border border-[#E5DFC8] p-5 transition-opacity duration-200 ${dim ? "opacity-50" : ""} ${className}`}
      data-testid={testid}
    >
      <p className="hv-heading font-bold text-hv-navy flex items-center gap-2">
        <Icon className="w-4 h-4 text-hv-teal" strokeWidth={2.4} /> {title}
      </p>
      {sub && <p className="text-xs text-hv-slate mt-0.5">{sub}</p>}
      <div className="mt-4">{children}</div>
    </div>
  );
}

function BrandTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-white border border-[#E5DFC8] px-3 py-2 shadow-lg text-xs">
      <div className="font-bold text-hv-navy mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5 text-hv-navy">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />
          {p.name}: <span className="font-bold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

const axisTick = { fill: SLATE, fontSize: 11 };

function RankedBarList({ rows, testid, color = TEAL }) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  if (!rows.length) return <p className="text-hv-slate text-sm">No data yet.</p>;
  return (
    <div className="space-y-3" data-testid={testid}>
      {rows.map((r) => (
        <div key={r.label} data-testid={`${testid}-row-${r.label}`}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="font-bold text-hv-navy">{r.label}</span>
            <span className="text-hv-slate">{r.value}</span>
          </div>
          <div className="h-2.5 rounded-full bg-hv-cream overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.max(4, (r.value / max) * 100)}%`, background: color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Caps a long-tail category at the top N + an "Other" bucket, per the brief.
function topNPlusOther(entries, n = 6) {
  const sorted = [...entries].sort((a, b) => b.value - a.value);
  if (sorted.length <= n) return sorted;
  const head = sorted.slice(0, n);
  const otherTotal = sorted.slice(n).reduce((s, r) => s + r.value, 0);
  return otherTotal > 0 ? [...head, { label: "Other", value: otherTotal }] : head;
}

export default function AdminAnalytics() {
  // Lifetime data - fetched once, never re-fetched by the period selector.
  const [lifetime, setLifetime] = useState(null);
  const [lifetimeErr, setLifetimeErr] = useState("");

  // Period-scoped data.
  const [period, setPeriod] = useState("weekly");
  const [offset, setOffset] = useState(0);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [summary, setSummary] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [summaryErr, setSummaryErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/admin/analytics");
        setLifetime(data);
      } catch (ex) {
        setLifetimeErr(formatApiError(ex.response?.data?.detail) || ex.message);
      }
    })();
  }, []);

  const loadSummary = useCallback(async () => {
    if (period === "custom" && (!customStart || !customEnd)) return;
    setFetching(true);
    setSummaryErr("");
    try {
      const params = { period, offset };
      if (period === "custom") { params.start = customStart; params.end = customEnd; }
      const { data } = await client.get("/admin/analytics/summary", { params });
      setSummary(data);
    } catch (ex) {
      setSummaryErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setFetching(false);
    }
  }, [period, offset, customStart, customEnd]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const selectPeriod = (p) => {
    setPeriod(p);
    setOffset(0);
  };

  const exportCsv = async (path, filename) => {
    try {
      const res = await client.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (ex) {
      setSummaryErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const roleRows = useMemo(() => {
    if (!lifetime?.users_by_role) return [];
    return Object.entries(lifetime.users_by_role)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({ label: ROLE_LABEL[k] || k, value: v }))
      .sort((a, b) => b.value - a.value);
  }, [lifetime]);

  const langRows = useMemo(() => {
    if (!lifetime?.users_by_language) return [];
    const entries = Object.entries(lifetime.users_by_language).map(([k, v]) => ({ label: LANG_LABEL[k] || k, value: v }));
    return topNPlusOther(entries, 6);
  }, [lifetime]);

  if (lifetimeErr) return <div className="text-hv-coral font-bold text-sm">{lifetimeErr}</div>;
  if (!lifetime) return <p className="text-hv-slate text-sm">Loading…</p>;

  const m = summary?.metrics;

  return (
    <div data-testid="admin-analytics">
      <div className="mb-6">
        <h1 className="hv-heading text-2xl sm:text-3xl text-hv-navy">Analytics</h1>
        <p className="text-sm text-hv-slate mt-1">Growth, activity, and progress for a selected time window</p>
      </div>

      {/* ---- Period selector ---- */}
      <div className="rounded-2xl bg-white border border-[#E5DFC8] p-4 mb-6" data-testid="period-bar">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1.5" data-testid="period-tabs">
            {PERIODS.map((p) => (
              <button
                key={p.v}
                onClick={() => selectPeriod(p.v)}
                data-testid={`period-${p.v}`}
                className={`rounded-full px-4 py-2 text-sm hv-heading font-semibold transition min-h-[38px] ${
                  period === p.v ? "bg-hv-navy text-white" : "bg-white border border-[#E5DFC8] text-hv-navy hover:bg-hv-cream"
                }`}
              >
                {p.l}
              </button>
            ))}
          </div>

          {period !== "custom" && (
            <div className="flex items-center gap-2" data-testid="period-nav">
              <button
                onClick={() => setOffset((o) => o + 1)}
                title="Previous period"
                data-testid="period-prev"
                className="rounded-full p-2 border border-[#E5DFC8] text-hv-navy hover:bg-hv-cream"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={2.6} />
              </button>
              <span className="hv-heading text-hv-navy text-sm min-w-[180px] text-center" data-testid="period-label">
                {summary?.range?.label || "…"}
              </span>
              <button
                onClick={() => setOffset((o) => Math.max(0, o - 1))}
                disabled={offset === 0}
                title="Next period"
                data-testid="period-next"
                className="rounded-full p-2 border border-[#E5DFC8] text-hv-navy hover:bg-hv-cream disabled:opacity-30 disabled:hover:bg-white"
              >
                <ChevronRight className="w-4 h-4" strokeWidth={2.6} />
              </button>
              {offset !== 0 && (
                <button
                  onClick={() => setOffset(0)}
                  data-testid="period-jump-current"
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold text-hv-teal border border-hv-teal/40 hover:bg-hv-tealTint"
                >
                  <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.6} /> Jump to current
                </button>
              )}
            </div>
          )}

          {period === "custom" && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="custom-range">
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                     data-testid="custom-start" className="rounded-full border border-[#E5DFC8] px-3 py-2 text-sm min-h-[38px]" />
              <span className="text-hv-slate text-sm">to</span>
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                     data-testid="custom-end" className="rounded-full border border-[#E5DFC8] px-3 py-2 text-sm min-h-[38px]" />
              {summary?.range?.label && (
                <span className="hv-heading text-hv-navy text-sm ms-1" data-testid="period-label">{summary.range.label}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {summaryErr && <div className="text-hv-coral font-bold text-sm mb-4" data-testid="summary-error">{summaryErr}</div>}

      <div className="space-y-6">
        {/* ---- Row 1: totals ---- */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatTile icon={Users} value={lifetime.total_users} label="Total users" lifetime />
          <MetricTile icon={UserPlus} value={m ? m.new_users.value : "—"} label="New users" delta={m?.new_users} />
          <MetricTile icon={Activity} value={m ? m.active_users.value : "—"} label="Active users" delta={m?.active_users} />
          <MetricTile icon={ClipboardCheck} value={m ? m.attempts.value : "—"} label="Quiz attempts" delta={m?.attempts} />
          <MetricTile icon={CheckCircle2} value={m ? m.completions.value : "—"} label="Completions" delta={m?.completions} />
        </div>

        {/* Secondary strip - kept from the old page, demoted here since they
            don't fit the period-scoped row above (they're lifetime counts). */}
        <div className="grid grid-cols-2 gap-4 max-w-lg">
          <StatTile icon={BookOpen} value={lifetime.active_cohorts} label="Active cohorts" lifetime />
          <StatTile icon={GraduationCap} value={lifetime.completed_progress_count} label="Stories completed" lifetime />
        </div>

        {/* ---- Row 2: growth (large) + cumulative (own axis) ---- */}
        <div className="grid lg:grid-cols-3 gap-5">
          <ChartCard
            icon={BarChart3} title="New signups" sub={summary ? `Per ${summary.bucket}, ${summary.range.label}` : ""}
            className="lg:col-span-2" dim={fetching} testid="chart-growth"
          >
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary?.growth_series || []} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={BORDER} />
                  <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: BORDER }} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<BrandTooltip />} cursor={{ fill: "rgba(64,152,154,0.08)" }} />
                  <Bar dataKey="value" name="New users" fill={TEAL} radius={[6, 6, 0, 0]} maxBarSize={44} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard
            icon={TrendingUp} title="Cumulative total" sub="Lifetime running total, own scale"
            dim={fetching} testid="chart-cumulative"
          >
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={summary?.cumulative_series || []} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cumFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={CORAL} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={CORAL} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke={BORDER} />
                  <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: BORDER }} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<BrandTooltip />} cursor={{ stroke: CORAL, strokeWidth: 1 }} />
                  <Area type="monotone" dataKey="value" name="Total users" stroke={CORAL} fill="url(#cumFill)" strokeWidth={2.5} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>

        {/* ---- Row 3: activity ---- */}
        <div className="grid lg:grid-cols-2 gap-5">
          <ChartCard icon={Activity} title="Active users" sub={summary ? `Per ${summary.bucket}` : ""} dim={fetching} testid="chart-active">
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary?.active_series || []} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={BORDER} />
                  <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: BORDER }} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<BrandTooltip />} cursor={{ fill: "rgba(64,152,154,0.08)" }} />
                  <Bar dataKey="value" name="Active users" fill={TEAL} radius={[6, 6, 0, 0]} maxBarSize={40} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard icon={Layers} title="Quiz attempts vs completions" sub={summary ? `Per ${summary.bucket}` : ""} dim={fetching} testid="chart-attempts">
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary?.attempts_series || []} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={BORDER} />
                  <XAxis dataKey="label" tick={axisTick} axisLine={{ stroke: BORDER }} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<BrandTooltip />} cursor={{ fill: "rgba(64,152,154,0.08)" }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: SLATE }} iconType="circle" iconSize={8} />
                  <Bar dataKey="attempts" name="Attempts" fill={TEAL} radius={[6, 6, 0, 0]} maxBarSize={20} isAnimationActive={false} />
                  <Bar dataKey="completions" name="Completions" fill={CORAL} radius={[6, 6, 0, 0]} maxBarSize={20} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>

        {/* ---- Row 4: story performance (lifetime - never re-fetched) ---- */}
        <div className="rounded-2xl bg-white border border-[#E5DFC8] overflow-hidden" data-testid="chart-story-performance">
          <p className="hv-heading font-bold text-hv-navy px-5 pt-5">Quiz pass rate by story</p>
          <p className="text-xs text-hv-slate px-5 mt-0.5">All-time, unaffected by the period selector above.</p>
          {lifetime.pass_rates_by_story.length === 0 ? (
            <div className="p-10 text-center">
              <p className="hv-heading text-lg text-hv-navy">No modules yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5DFC8] text-start text-hv-slate">
                    <th className="px-5 py-3 hv-heading font-semibold text-start">Story</th>
                    <th className="px-5 py-3 hv-heading font-semibold text-start">Attempts</th>
                    <th className="px-5 py-3 hv-heading font-semibold text-start">Passed</th>
                    <th className="px-5 py-3 hv-heading font-semibold text-start">Pass rate</th>
                  </tr>
                </thead>
                <tbody>
                  {lifetime.pass_rates_by_story.map((r) => (
                    <tr key={r.global_sequence} className="border-b border-[#E5DFC8] last:border-0">
                      <td className="px-5 py-3 font-bold text-hv-navy">#{r.global_sequence}</td>
                      <td className="px-5 py-3">{r.attempts}</td>
                      <td className="px-5 py-3">{r.passed}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-24 rounded-full bg-hv-cream overflow-hidden">
                            <div className="h-full bg-hv-teal" style={{ width: `${r.pass_rate}%` }} />
                          </div>
                          <span className="text-xs font-bold text-hv-navy">{r.pass_rate}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ---- Row 5: demographics (lifetime - never re-fetched) ---- */}
        <div className="grid lg:grid-cols-2 gap-5">
          <ChartCard icon={Users} title="Users by role" sub="All-time" testid="demo-role">
            <RankedBarList rows={roleRows} testid="demo-role-list" color={TEAL} />
          </ChartCard>
          <ChartCard icon={LineChartIcon} title="Users by language" sub="All-time, top 6 + Other" testid="demo-language">
            <RankedBarList rows={langRows} testid="demo-language-list" color={CORAL} />
          </ChartCard>
        </div>

        {/* ---- Downloadable reports (unchanged) ---- */}
        <div className="rounded-2xl bg-white border border-[#E5DFC8] p-5">
          <p className="hv-heading font-bold text-hv-navy mb-4">Downloadable reports</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <button onClick={() => exportCsv("/admin/analytics/export/users.csv", "users.csv")}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-hv-coral text-white px-5 py-2.5 hv-heading">
                <Download size={16} /> User progress CSV
              </button>
            </div>
            <div>
              <button onClick={() => exportCsv("/admin/analytics/export/progress.csv", "progress.csv")}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white border border-[#E5DFC8] text-hv-navy px-5 py-2.5 hv-heading hover:bg-hv-cream">
                <Download size={16} /> Module progress CSV
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
